/**
 * 関東の国 — 相模・武蔵・下総・常陸
 *
 * 盤面 10・11・13・14 番。docs/01-ゲームデザイン.md §2.2 の表に従う。
 * 史実の書きぶりは docs/05-学習設計.md §4 と _reference.ts に揃えること。
 *
 * 石高の実値は慶長3年（1598年）の国別集計による。
 *   相模 194,204石／武蔵 667,126石／下総 393,255石／常陸 530,008石
 *   （武蔵は陸奥・近江に次ぐ全国3位、常陸は全国7位）
 */

import type { Province } from '../types';

/** 相模湾に臨む国。地の利は湊（鉄砲／第I章は足軽）。北条氏の本拠。 */
export const SAGAMI: Province = {
  id: 10,
  kind: 'province',
  name: '相模',
  reading: 'さがみ',
  region: 'kanto',
  kokudaka: 3,
  defense: 3,
  terrain: 'coast',
  historicalKokudaka: 19.4,
  note: {
    summary:
      '相模湾に臨む国。鎌倉と小田原という二つの都市を抱え、伊勢宗瑞（北条早雲）にはじまる北条氏が五代にわたって関東支配の拠点とした。',
    detail: {
      1: 'いまの神奈川県のうち、川崎や横浜のあたり（武蔵国）をのぞいた地いきにあたる国です。海に面していて、鎌倉や小田原といったまちがありました。北条氏が小田原城を本きょにして、関東の広いはんいをおさめました。ねんぐを軽くするきまりを出して、村の人たちに直接知らせたことでも知られています。',
      2: '現在の神奈川県のうち、東部（川崎・横浜のあたり。ここは武蔵国）を除いた地域にあたる国。相模湾に面し、東に三浦半島、西に箱根の山地を持つ。石高は約19万石で多くはないが、鎌倉と小田原という都市を抱え、海運と東海道の交通で栄えた。15世紀末に伊勢宗瑞（のちに北条早雲と呼ばれる）が伊豆から進出して小田原城を得て、1516年に三浦氏を破って相模を平定した。以後、氏綱・氏康・氏政・氏直と五代にわたり小田原を拠点とする。北条氏は領内で検地を行い、1550年には畠にかかる諸税を「懸銭」に一本化して負担を軽くする法令を出し、虎の印判を押した文書で百姓に直接知らせた。1590年、豊臣秀吉の軍に小田原城を囲まれて北条氏は降伏した。',
      3: '現在の神奈川県のうち、東部の川崎・横浜のあたり（武蔵国域）を除いた地域。慶長3年（1598年）の集計では約19.4万石で、石高だけを見れば大国ではない。しかし相模湾の海運、三浦半島の湊、東海道と鎌倉街道が交わる位置が国の価値を高めていた。備中伊勢氏の出で室町幕府に仕えた伊勢盛時（宗瑞）が伊豆を得たのち小田原城を手に入れ、永正13年（1516年）に三浦義同・義意を新井城に攻めて相模一国を支配下に置いた。子の氏綱の代から「北条」を称し、氏康・氏政・氏直と続く。北条氏の支配を特徴づけるのは文書行政と税制で、永正15年（1518年）から「禄寿応穏」の四字を刻んだ虎の印判状を用い、代官を介さずに百姓へ直接命令を伝えた。天文19年（1550年）の公事赦免令では畠地の諸点役を廃して貫高の6％にあたる懸銭に一本化し、棟別銭も減免し、退転した百姓の債務破棄を認めている。永禄2年（1559年）には家臣の所領を貫高で書き上げた『小田原衆所領役帳』が作られ、家臣団と知行の実態を知る基本史料となっている。婚姻も外交の手段で、氏康の娘（早川殿）は今川氏真に嫁ぎ、武田信玄の娘（黄梅院）は氏政に嫁いだが、同盟の破棄とともに離縁された。1590年の小田原合戦で北条氏は大名としての地位を失い、相模は徳川氏の領国に組み込まれた。',
    },
    year: [1467, 1590],
    keywords: ['相模', '北条氏', '小田原城', '鎌倉', '虎の印判状', '三浦氏'],
    caveat:
      '伊勢宗瑞（北条早雲）については、素浪人から成り上がったという話が長く語られてきましたが、室町幕府に仕えた備中伊勢氏の一族であることが研究で明らかになっています。生年も従来の1432年説に対して1456年説が出され、小田原城を手に入れた時期も通説の1495年に疑問が示されるなど、確定していない点が多く残ります。また本人は生涯「伊勢」を名乗っており、「北条」を称したのは子の氏綱の代からです。',
    gameNote:
      '盤上の石高ランク3は遊びやすさのために調整した相対値です。実際の相模は慶長3年（1598年）の集計で約19.4万石で、盤上でランク2としている下総（約39.3万石）よりも小さな国でした。盤上で高めに置いているのは、小田原を拠点とした北条氏の勢力と海運の富を盤面で表すためです。地形は相模湾に面することから「湊」に分類していますが、実際の相模には箱根・丹沢の山地も広く含まれます。',
  },
};

/** 関東平野の中心を占める国。地の利は騎馬。盤上の最大石高。 */
export const MUSASHI: Province = {
  id: 11,
  kind: 'province',
  name: '武蔵',
  reading: 'むさし',
  region: 'kanto',
  kokudaka: 4,
  defense: 3,
  terrain: 'plain',
  historicalKokudaka: 66.7,
  note: {
    summary:
      '関東平野の中心を占める広大な国。石高は全国有数で、江戸・河越などの城が置かれ、関東の争いの主戦場となった。',
    detail: {
      1: 'いまの東京都と埼玉県、神奈川県の東がわにあたる国です。関東平野のまん中にあり、広くて米がよくとれました。太田道灌がきずいた江戸城があり、のちに徳川家康がここに入って、江戸のまちがはじまります。秩父でとれる石は、村の人たちが供養のためにたてた石のとうばに使われました。',
      2: '現在の東京都・埼玉県と神奈川県東部にあたる国。関東平野の中心を占め、慶長期の石高は約67万石で全国第3位にあたる。荒川・入間川・多摩川の流域に水田が開けた一方、武蔵野台地は水が乏しく、広い原野が残っていた。秩父の山から切り出される緑泥石片岩は「板碑」と呼ばれる供養塔に加工され、武士だけでなく村の百姓が仲間を集めて共同で建てた例が多く残る。1457年に太田道灌が江戸城を築き、河越城も同じころ扇谷上杉氏のもとで築かれた。1454年に始まる享徳の乱以来、関東は古河公方と上杉氏に分かれて争い、武蔵はその主戦場となった。1546年の河越城の戦いののち北条氏の支配が広がる。1590年、豊臣秀吉の軍が関東に入り、忍城などが攻められた。同じ年、徳川家康が江戸に入った。',
      3: '現在の東京都・埼玉県と神奈川県東部。慶長3年（1598年）の集計で約66.7万石にのぼり、陸奥・近江に次ぐ全国第3位の大国である。荒川・入間川・多摩川の沖積地に水田が開かれる一方、水利に乏しい武蔵野台地には近世の用水開削まで広い原野が残った。中世の武蔵を特徴づける遺物に「武蔵型板碑」がある。秩父産の緑泥石片岩を薄く切り出した供養塔で、14〜15世紀には武士のほか、村の百姓が講を結んで共同で造立した例が数多く確認され、地域の人びとの信仰と結びつきを伝える。享徳3年（1454年）に始まる享徳の乱以降、関東は古河公方足利氏と関東管領上杉氏に二分され、扇谷上杉氏の家宰であった太田道灌は長禄元年（1457年）に江戸城を築いた。16世紀に入ると小田原の北条氏が北上し、天文15年（1546年）の河越城の戦いを経て武蔵の大半を支配下に置く。天正18年（1590年）の小田原合戦では、忍城が堤を築いての水攻めを受けながら落城せず、小田原の降伏を受けて開城した。同年、徳川家康が関東へ移されて江戸城に入り、以後の江戸の発展につながっていく。',
    },
    year: [1467, 1600],
    keywords: ['武蔵', '関東平野', '江戸城', '太田道灌', '板碑', '河越城'],
    caveat:
      '1546年の河越城の戦いで、北条氏康が寡兵で八万の大軍を破ったという「河越夜戦」の話は、江戸時代の軍記によって広まったものです。同時代の史料が乏しく、兵数も経過もよくわかっておらず、合戦の規模そのものを疑う説もあります。また1590年の忍城の籠城で成田氏の娘・甲斐姫が武器を取って戦ったという話は、19世紀初めに成立した『成田記』に現れるもので、同時代の記録では確かめられません。水攻めについても、書状から秀吉の指示によるものであったことが知られており、石田三成の発案とする軍記の記述とは食い違います。',
    gameNote:
      '盤上では最大の石高ランク4を与えており、この点は実際の武蔵が慶長3年（1598年）の集計で約66.7万石と全国3位だったことに沿っています。ただし盤上の数値そのものは遊びやすさのための相対値で、石高の比をそのまま表したものではありません。地形は関東平野を採って「平野」としていますが、実際の武蔵国には秩父の山地や、水の乏しい武蔵野台地も含まれます。',
  },
};

/** 香取海と江戸湾にはさまれた国。地の利は湊（鉄砲／第I章は足軽）。 */
export const SHIMOUSA: Province = {
  id: 13,
  kind: 'province',
  name: '下総',
  reading: 'しもうさ',
  region: 'kanto',
  kokudaka: 2,
  defense: 2,
  terrain: 'coast',
  historicalKokudaka: 39.3,
  note: {
    summary:
      '香取海と江戸湾にはさまれた水運の国。千葉氏が本佐倉城を拠点とし、関宿・国府台など川と海の要地をめぐって北条・里見・上杉が争った。',
    detail: {
      1: 'いまの千葉県の北がわと茨城県の南西がわにあたる国です。まわりを川や海にかこまれていて、船で物を運ぶ仕事がさかんでした。北東には「香取の海」という大きな入り海があり、漁や船の仕事をする人びとが住んでいました。今の利根川の流れは、江戸時代の工事でつくられたものです。',
      2: '現在の千葉県北部、茨城県南西部、埼玉県東部および東京都の東端にあたる国。北東には「香取海」と呼ばれる広い内海が広がり、西は江戸湾に面していて、陸路よりも水運が重んじられた。現在の利根川が銚子で太平洋に注ぐ流路は江戸時代の東遷工事による結果で、戦国時代の川筋は今と大きく違う。香取神宮は内海沿岸の津（港）と、そこで働く海夫を支配下に置いていた。桓武平氏の流れをくむ千葉氏が守護をつとめたが、15世紀半ばの内紛で宗家が衰え、文明年間に本佐倉城へ本拠を移したのちは家臣の原氏らが実権を握った。1538年と1564年の二度の国府台の戦いでは、江戸川ぞいの台地で北条氏と里見氏らが戦った。水運の要である関宿城をめぐる争いは三度に及び、1574年に北条氏が手に入れている。',
      3: '現在の千葉県北部、茨城県南西部、埼玉県東部および東京都の東端。慶長3年（1598年）の集計で約39.3万石。北東には「香取海」と呼ばれる広い内海があり、西は江戸湾に面していて、陸路よりも水運が優越する国であった。現在の利根川が銚子で太平洋へ注ぐ流路は17世紀以降の利根川東遷事業によって作られたもので、戦国期には利根川筋の水が江戸湾へ流れ込み、香取海には常陸川・太日川などが注いでいた。香取神宮に伝わる「海夫注文」は、内海沿岸の津とそこに属する海夫を書き上げた文書で、漁や舟運に従事した人びとの存在と、それを社が支配していた実態を具体的に示す。守護は桓武平氏の流れをくむ千葉氏だが、享徳の乱に連動した内紛で宗家が倒れ、以後は原氏らの家臣が実権を持った。千葉輔胤が文明年間（1469〜87年）に本佐倉城を築いてからは、ここが下総の政治の中心となる。天文7年（1538年）の第一次国府台合戦では北条氏綱と小弓公方足利義明・里見氏が戦って義明が戦死し、永禄7年（1564年）の第二次では北条氏康が里見義弘を退けた。利根川水系の分岐点にあたる関宿城は、簗田氏との三度の攻防の末、天正2年（1574年）に北条氏の手に落ちている。1590年に千葉氏は所領を失い、下総は徳川氏の支配下に入った。',
    },
    year: [1467, 1590],
    keywords: ['下総', '千葉氏', '香取海', '本佐倉城', '関宿城', '国府台'],
    caveat:
      '関宿について「ここを抑えることは一国を取るのに等しい」と北条氏康が述べたと伝えられますが、これは後世の記録に載るもので、氏康自身の言葉であるという確証はありません。また現在の利根川の流路は江戸時代以降の工事によるもので、戦国時代の地理を今の地図にそのまま重ねることはできません。香取海も干拓と土砂の堆積によって縮み、いまは霞ヶ浦・北浦や水田として残るのみです。',
    gameNote:
      '盤上の石高ランク2は遊びやすさのために調整した相対値です。実際の下総は慶長3年（1598年）の集計で約39.3万石あり、盤上でランク3としている相模（約19.4万石）よりも大きな国でした。盤上の数値は史実の石高の順位を表すものではありません。地形は香取海と江戸湾にはさまれた水運の国として「湊」に分類していますが、実際の下総の大半は下総台地とその周辺の低地からなります。',
  },
};

/** 関東北東部の大国。地の利は騎馬。佐竹氏が北から国内をまとめた。 */
export const HITACHI: Province = {
  id: 14,
  kind: 'province',
  name: '常陸',
  reading: 'ひたち',
  region: 'kanto',
  kokudaka: 3,
  defense: 3,
  terrain: 'plain',
  historicalKokudaka: 53,
  note: {
    summary:
      '関東の北東部を占める大国。広い平野と霞ヶ浦・鹿島灘の水運を持ち、北の佐竹氏と中南部の大掾氏・小田氏らが長く勢力を分け合った。',
    detail: {
      1: 'いまの茨城県のほとんどにあたる国です。広い平野があって米がよくとれ、霞ヶ浦という大きな湖や海では魚がとれました。北のほうをおさめた佐竹氏が、豊臣秀吉のころに国じゅうをまとめて、水戸城をあらたな本きょにしました。',
      2: '現在の茨城県の大部分にあたる国。慶長期の石高は約53万石で全国でも上位に入る。那珂川・久慈川の流域に平野が開け、南には霞ヶ浦・北浦が広がって舟運と漁業が営まれた。鹿島神宮などの社は水辺の信仰を集め、津の支配とも結びついていた。国の北部は清和源氏の流れをくむ佐竹氏、中部は国衙の役職に由来する大掾氏、南部は小田氏が押さえ、16世紀を通じて争った。佐竹義重は北の伊達氏や南の北条氏と対立し、その子義宣は1590年の小田原攻めに参陣して豊臣秀吉から常陸の支配を認められると、水戸城の江戸氏や南部の領主を退けて国内をまとめた。1591年には本拠を太田城から水戸城へ移している。',
      3: '現在の茨城県の大部分。慶長3年（1598年）の集計で約53万石、全国7位にあたる大国である。西に八溝山地を背負い、那珂川・久慈川・小貝川の流域に平野が広がり、南部には霞ヶ浦・北浦と、下総との境をなす香取海があって、内水面の舟運と漁業が生業として定着していた。鹿島神宮とその周辺の社は水運に関わる人びとの信仰を集め、沿岸の津の支配とも結びついている。中世の常陸は、清和源氏の流れをくむ佐竹氏が北部の太田城を拠点とし、国衙の在庁官人の系譜を引く大掾氏が中部に、八田氏の流れをくむ小田氏が南部にあって、それぞれ在地の領主を従える分立状態にあった。佐竹義重は伊達晴宗の娘を妻としており、その子義宣と伊達政宗は従兄弟にあたるが、南奥州の支配をめぐって対立している。天正18年（1590年）、義宣は豊臣秀吉の小田原攻めに参陣して常陸支配を承認され、水戸城の江戸氏や府中の大掾氏を退け、翌年には南部の鹿島・行方の領主層（のちに「南方三十三館」と呼ばれる）を討った。これによって常陸は初めて一人の大名のもとにまとまり、1591年には居城が太田城から水戸城へ移された。1594年には下野の一部と合わせて54万5千石余を領したが、1600年の関ヶ原の戦いで態度を明らかにしなかったため、1602年に出羽秋田へ移されている。',
    },
    year: [1467, 1602],
    keywords: ['常陸', '佐竹氏', '霞ヶ浦', '水戸城', '鹿島神宮', '太田城'],
    caveat:
      '「南方三十三館」という呼び方や、討たれた領主の数え方は後世の記録によるもので、同時代の史料で確かめられる範囲とは差があります。佐竹氏による常陸統一は、豊臣政権の権威を背景に在地の領主を排除して進められたもので、地域の側から見れば支配のかたちが短期間で大きく変わる出来事でした。',
    gameNote:
      '盤上の石高ランク3は遊びやすさのために調整した相対値です。実際の常陸は慶長3年（1598年）の集計で約53万石あり、全国7位の大国でした。地形は那珂川流域や南部の平野を採って「平野」としていますが、実際の常陸には霞ヶ浦・北浦の広い水域や八溝山地も含まれます。',
  },
};
